import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowDownTrayIcon, PrinterIcon, CalendarIcon, MagnifyingGlassIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import inventoryApi from '../../../services/inventoryApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import { downloadXlsxFromAoa } from '../../../utils/excelJsSheetHelpers';
import { getSlimLogoPath } from '../../../utils/slimLogo';
import adminAPI from '../../../services/adminApi';

/**
 * Stock Operations Report — the whole flow of every stock request.
 *  Transfers:         Goods Transfer Request → Goods Issue Note(s) → Transfer In(s)
 *  Material requests: Material Request Note → Consumption(s) released to a cost center
 * One row per request (stage, documents, quantities), expandable to its item lines.
 */

const TABS = [
  { key: 'transfers', label: 'Transfers', sub: 'Transfer Request → Issue Note → Transfer In' },
  { key: 'material', label: 'Material Requests', sub: 'Material Request → Consumption' }
];

const STATUS_OPTIONS = {
  transfers: [
    ['', 'All statuses'], ['draft', 'Draft'], ['pending', 'Pending'], ['requested', 'Requested'], ['approved', 'Approved'],
    ['converted_to_gi', 'Converted to issue'], ['partially_fulfilled', 'Partially fulfilled'], ['fulfilled', 'Fulfilled'],
    ['rejected', 'Rejected'], ['cancelled', 'Cancelled']
  ],
  material: [
    ['', 'All statuses'], ['draft', 'Draft'], ['pending', 'Pending'], ['partially_released', 'Partially released'],
    ['completed', 'Completed'], ['rejected', 'Rejected'], ['cancelled', 'Cancelled']
  ]
};

const STAGE_OPTIONS = {
  transfers: ['Draft', 'Awaiting approval', 'Approved — not issued', 'Issue note pending', 'Partially issued', 'In transit', 'Receipt pending', 'Partially received', 'Completed', 'Rejected', 'Cancelled'],
  material: ['Draft', 'Awaiting release', 'Partially released', 'Completed', 'Rejected', 'Cancelled']
};

const stageColor = (stage) => ({
  'Completed': 'bg-green-100 text-green-800 border-green-200',
  'Rejected': 'bg-red-100 text-red-800 border-red-200',
  'Cancelled': 'bg-gray-100 text-gray-700 border-gray-200',
  'Draft': 'bg-gray-100 text-gray-700 border-gray-200',
  'Awaiting approval': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Awaiting release': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'In transit': 'bg-blue-100 text-blue-800 border-blue-200',
  'Receipt pending': 'bg-blue-100 text-blue-800 border-blue-200',
  'Partially received': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'Partially released': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'Partially issued': 'bg-indigo-100 text-indigo-800 border-indigo-200'
}[stage] || 'bg-orange-100 text-orange-800 border-orange-200');

const docColor = (status) => ({
  in_transit: 'bg-blue-50 text-blue-700 border-blue-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  draft: 'bg-gray-50 text-gray-600 border-gray-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  returned: 'bg-orange-50 text-orange-700 border-orange-200'
}[status] || 'bg-gray-50 text-gray-600 border-gray-200');

const qty = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const pretty = (s) => String(s || '').replace(/_/g, ' ');
const ymd = (d) => d.toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); return ymd(new Date(Date.UTC(d.getFullYear(), d.getMonth(), 1))); };
const escapeHtml = (s) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const StockOperationsReport = () => {
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem('stockOperationsTab') || 'transfers'; } catch { return 'transfers'; }
  });
  const [filters, setFilters] = useState({ dateFrom: firstOfMonth(), dateTo: ymd(new Date()), status: '', stage: '', warehouseId: '' });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [warehouses, setWarehouses] = useState([]);
  const [locationInfo, setLocationInfo] = useState({});
  const timer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await inventoryApi.getWarehouses({ limit: 500 });
        // admin locations come back as { data: [...] } or { data: { locations: [...] } }
        const list = [res?.data, res?.data?.locations, res?.data?.data, res?.locations, res].find(Array.isArray) || [];
        setWarehouses(list);
      } catch (e) {
        console.warn('Could not load warehouses:', e.message);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminAPI.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setLocationInfo({ name: loc.name || loc.locationName || '', street: addr.street || addr.addressLine1 || '', city: addr.city || '', zipCode: addr.zipCode || addr.postalCode || '' });
      } catch (e) {
        console.warn('[StockOperationsReport] could not load location info:', e.message);
      }
    })();
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => timer.current && clearTimeout(timer.current);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { ...filters, search: search || undefined };
        Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
        const res = tab === 'transfers'
          ? await inventoryApi.getStockOperationsTransfers(params)
          : await inventoryApi.getStockOperationsMaterialRequests(params);
        if (!cancelled) { setData(res.data || { rows: [], summary: {} }); setExpanded(new Set()); }
      } catch (err) {
        if (!cancelled) { toast.error(err.response?.data?.message || 'Failed to load the report'); setData({ rows: [], summary: {} }); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, filters, search]);

  const nameOf = useMemo(() => {
    const m = new Map();
    (Array.isArray(warehouses) ? warehouses : []).forEach(w => m.set(String(w._id), w.name || w.locationName || w.code));
    return (text, ids = []) => text || ids.map(id => m.get(String(id))).filter(Boolean).join(' · ') || '—';
  }, [warehouses]);

  const switchTab = (key) => {
    setTab(key);
    setFilters(f => ({ ...f, status: '', stage: '' }));
    try { localStorage.setItem('stockOperationsTab', key); } catch { /* ignore */ }
  };
  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const reset = () => { setFilters({ dateFrom: firstOfMonth(), dateTo: ymd(new Date()), status: '', stage: '', warehouseId: '' }); setSearchInput(''); };
  const toggle = (id) => setExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOpen = data.rows.length > 0 && data.rows.every(r => expanded.has(r._id));
  const toggleAll = () => setExpanded(allOpen ? new Set() : new Set(data.rows.map(r => r._id)));

  const isTransfers = tab === 'transfers';
  const summary = data.summary || {};
  const period = `${filters.dateFrom ? fmtDate(filters.dateFrom) : 'Start'} — ${filters.dateTo ? fmtDate(filters.dateTo) : 'Today'}`;

  // ── Exports ───────────────────────────────────────────────────────────────
  const exportExcel = async () => {
    const rows = [
      [`STOCK OPERATIONS REPORT — ${isTransfers ? 'TRANSFERS' : 'MATERIAL REQUESTS'}`],
      [`Period: ${period}`],
      [`Generated: ${new Date().toLocaleString()}`],
      []
    ];
    if (isTransfers) {
      rows.push(['Request No', 'Request Date', 'Requested By', 'From', 'To', 'Status', 'Stage', 'Issue Notes', 'Transfer Ins',
        'Item Code', 'Item', 'UOM', 'Requested', 'Approved', 'Issued', 'Received', 'Short / Damaged', 'In Transit', 'Pending Issue', 'Issued Value', 'Line Stage']);
      data.rows.forEach(r => {
        const head = [r.requestNo, fmtDate(r.requestDate), r.requestedBy, nameOf(r.source, r.sourceIds), nameOf(r.destination, r.destinationIds),
          pretty(r.status), r.stage, r.issueNotes.map(g => `${g.number} (${pretty(g.status)})`).join(', '), r.receipts.map(t => `${t.number} (${pretty(t.status)})`).join(', ')];
        (r.lines.length ? r.lines : [{}]).forEach(l => rows.push([...head, l.code || '', l.name || '', l.uom || '', l.requested ?? '', l.approved ?? '', l.issued ?? '',
          l.received ?? '', l.short ?? '', l.inTransit ?? '', l.pendingIssue ?? '', l.value ?? '', l.stage || '']));
      });
    } else {
      rows.push(['MRN No', 'Request Date', 'Requested By', 'Cost Center', 'Warehouse', 'Status', 'Stage', 'Consumptions',
        'Item Code', 'Item', 'UOM', 'Requested', 'Released', 'Pending', 'Cost', 'Line Stage']);
      data.rows.forEach(r => {
        const head = [r.requestNo, fmtDate(r.requestDate), r.requestedBy, r.costCenter, nameOf(r.warehouse, r.warehouseIds), pretty(r.status), r.stage,
          r.releases.map(c => c.number).join(', ')];
        (r.lines.length ? r.lines : [{}]).forEach(l => rows.push([...head, l.code || '', l.name || '', l.uom || '', l.requested ?? '', l.released ?? '', l.pending ?? '', l.cost ?? '', l.stage || '']));
      });
    }
    const widths = (isTransfers ? [16, 13, 18, 30, 30, 16, 20, 28, 28, 14, 30, 8, 10, 10, 10, 10, 12, 10, 12, 14, 20] : [16, 13, 18, 22, 30, 16, 18, 28, 14, 30, 8, 10, 10, 10, 14, 18]).map(wch => ({ wch }));
    await downloadXlsxFromAoa(rows, isTransfers ? 'Transfers' : 'Material Requests', `stock-operations-${tab}-${ymd(new Date())}.xlsx`, widths);
  };

  // Print / PDF — same letterhead template as the finance and inventory reports
  const printReport = () => {
    if (!data.rows.length) return;
    const logoUrl = window.location.origin + getSlimLogoPath();
    const watermarkUrl = window.location.origin + '/SLIM%20LOGO%20BLACK.png';
    const generatedAt = new Date().toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
    });
    const title = isTransfers ? 'Stock Operations — Transfers' : 'Stock Operations — Material Requests';
    const flowLabel = isTransfers ? 'Transfer Request → Goods Issue Note → Transfer In' : 'Material Request → Consumption';
    const whName = filters.warehouseId ? (warehouses.find(w => String(w._id) === String(filters.warehouseId))?.name || '—') : 'All';
    const e = escapeHtml;
    const n = (v) => (v ? qty(v) : '—');

    const body = data.rows.map((r, i) => {
      const where = isTransfers
        ? `${e(nameOf(r.source, r.sourceIds))} &rarr; ${e(nameOf(r.destination, r.destinationIds))}`
        : `${e(r.costCenter || '—')} &middot; ${e(nameOf(r.warehouse, r.warehouseIds))}`;
      const docs = isTransfers
        ? `<b>Issue:</b> ${r.issueNotes.map(g => `${e(g.number)} (${e(pretty(g.status))})`).join(', ') || '—'} &nbsp; <b>Receipt:</b> ${r.receipts.map(t => `${e(t.number)} (${e(pretty(t.status))})`).join(', ') || '—'}`
        : `<b>Released:</b> ${r.releases.map(c => `${e(c.number)} (${fmtDate(c.date)})`).join(', ') || '—'}`;
      const head = isTransfers
        ? `<tr class="req"><td class="center">${i + 1}</td>
            <td><span class="doc">${e(r.requestNo)}</span><div class="sub">${fmtDate(r.requestDate)}${r.requestedBy ? ' &middot; ' + e(r.requestedBy) : ''}</div></td>
            <td colspan="2">${where}<div class="sub">${docs}</div></td>
            <td class="right">${n(r.totals.requested)}</td><td class="right">${n(r.totals.approved)}</td><td class="right">${n(r.totals.issued)}</td>
            <td class="right">${n(r.totals.received)}</td><td class="right">${n(r.totals.short)}</td><td class="right">${n(r.totals.inTransit)}</td>
            <td class="right">${n(r.totals.pendingIssue)}</td><td><span class="stage">${e(r.stage)}</span></td></tr>`
        : `<tr class="req"><td class="center">${i + 1}</td>
            <td><span class="doc">${e(r.requestNo)}</span><div class="sub">${fmtDate(r.requestDate)}${r.requestedBy ? ' &middot; ' + e(r.requestedBy) : ''}</div></td>
            <td colspan="2">${where}<div class="sub">${docs}</div></td>
            <td class="right">${n(r.totals.requested)}</td><td class="right">${n(r.totals.released)}</td><td class="right">${n(r.totals.pending)}</td>
            <td class="right">${r.totals.cost ? money(r.totals.cost) : '—'}</td><td><span class="stage">${e(r.stage)}</span></td></tr>`;
      const lines = r.lines.map(l => isTransfers
        ? `<tr class="ln"><td></td><td class="code">${e(l.code)}</td><td colspan="2">${e(l.name)}${l.uom ? ' <span class="uom">' + e(l.uom) + '</span>' : ''}</td>
            <td class="right">${n(l.requested)}</td><td class="right">${n(l.approved)}</td><td class="right">${n(l.issued)}</td><td class="right">${n(l.received)}</td>
            <td class="right">${n(l.short)}</td><td class="right">${n(l.inTransit)}</td><td class="right">${n(l.pendingIssue)}</td><td class="lstage">${e(l.stage)}</td></tr>`
        : `<tr class="ln"><td></td><td class="code">${e(l.code)}</td><td colspan="2">${e(l.name)}${l.uom ? ' <span class="uom">' + e(l.uom) + '</span>' : ''}</td>
            <td class="right">${n(l.requested)}</td><td class="right">${n(l.released)}</td><td class="right">${n(l.pending)}</td>
            <td class="right">${l.cost ? money(l.cost) : '—'}</td><td class="lstage">${e(l.stage)}</td></tr>`).join('');
      return head + lines;
    }).join('');

    const colgroup = isTransfers
      ? '<col style="width:28px"/><col style="width:110px"/><col/><col style="width:120px"/>' + '<col style="width:62px"/>'.repeat(7) + '<col style="width:105px"/>'
      : '<col style="width:28px"/><col style="width:110px"/><col/><col style="width:120px"/>' + '<col style="width:72px"/>'.repeat(3) + '<col style="width:90px"/><col style="width:105px"/>';
    const thead = isTransfers
      ? '<th class="center">#</th><th>Request</th><th colspan="2">From &rarr; To / Documents</th><th class="right">Requested</th><th class="right">Approved</th><th class="right">Issued</th><th class="right">Received</th><th class="right">Short</th><th class="right">In Transit</th><th class="right">Pending</th><th>Stage</th>'
      : '<th class="center">#</th><th>MRN</th><th colspan="2">Cost Center / Warehouse / Releases</th><th class="right">Requested</th><th class="right">Released</th><th class="right">Pending</th><th class="right">Cost</th><th>Stage</th>';
    const cols = isTransfers ? 12 : 9;

    const totals = isTransfers
      ? [['Requests', summary.requests || 0], ['Requested', qty(summary.requested)], ['Issued', qty(summary.issued)], ['Received', qty(summary.received)],
         ['Short / Damaged', qty(summary.short)], ['In Transit', qty(summary.inTransit)], ['Pending Issue', qty(summary.pendingIssue)]]
      : [['Requests', summary.requests || 0], ['Requested', qty(summary.requested)], ['Released', qty(summary.released)], ['Pending', qty(summary.pending)]];
    const grand = isTransfers ? ['Issued Value', 'Rs ' + money(summary.value)] : ['Total Cost', 'Rs ' + money(summary.cost)];
    const stages = Object.entries(summary.byStage || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => `<span class="chip">${e(k)}: ${v}</span>`).join(' ');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${title} — ${period}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 32px 36px; min-height: 100vh; display: flex; flex-direction: column; }
    .doc-main { flex: 1; }
    #toolbar { position: fixed; top: 0; left: 0; right: 0; z-index: 9999; background: #374151; padding: 8px 16px; display: flex; align-items: center; gap: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.18); }
    #toolbar .tb-title { color: #fff; font-size: 13px; font-weight: 600; flex: 1; }
    #toolbar button { padding: 6px 18px; border: none; border-radius: 4px; font-size: 12px; font-weight: 600; cursor: pointer; }
    .btn-pdf { background: #fff; color: #374151; } .btn-download { background: #4b5563; color: #fff; } .btn-close { background: rgba(255,255,255,0.15); color: #fff; }
    .tb-spacer { height: 46px; }
    @page { size: A4 landscape; margin: 0; }
    @media print { #toolbar, .tb-spacer { display: none !important; } body { padding: 12mm 14mm 20mm !important; display: block !important; } tr { page-break-inside: avoid; } }
    .top-banner { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 14px; border-bottom: 2px solid #374151; margin-bottom: 14px; }
    .company-block { display: flex; flex-direction: column; align-items: flex-start; gap: 3px; }
    .company-logo { height: 48px; width: auto; object-fit: contain; margin-bottom: 4px; }
    .company-name { font-size: 13px; font-weight: 700; color: #111; }
    .company-detail { font-size: 10px; color: #6b7280; line-height: 1.5; }
    .doc-block { text-align: right; }
    .doc-title { font-size: 22px; font-weight: 800; color: #374151; letter-spacing: -0.02em; }
    .doc-subtitle { font-size: 11px; color: #6b7280; margin-top: 3px; }
    .meta-row { display: grid; grid-template-columns: repeat(5, 1fr); border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; margin-bottom: 12px; }
    .meta-cell { padding: 8px 12px; border-right: 1px solid #e5e7eb; }
    .meta-cell:last-child { border-right: none; }
    .meta-cell label { display: block; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #9ca3af; margin-bottom: 3px; }
    .meta-cell span { font-size: 11px; font-weight: 600; color: #111; }
    .section-title { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #6b7280; margin-bottom: 6px; padding-bottom: 3px; border-bottom: 1px solid #e5e7eb; }
    .stages { margin-bottom: 12px; }
    .chip { display: inline-block; border: 1px solid #d1d5db; border-radius: 10px; padding: 2px 8px; margin: 0 4px 4px 0; font-size: 9px; color: #374151; background: #f9fafb; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .ops-table { width: 100%; border-collapse: collapse; margin-bottom: 16px; table-layout: fixed; border: 1px solid #d1d5db; }
    .ops-table th { background: #374151 !important; color: #fff !important; font-size: 8.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; padding: 7px 6px; text-align: left; border-right: 1px solid rgba(255,255,255,0.2); }
    .ops-table th:last-child { border-right: none; }
    .ops-table td { padding: 5px 6px; font-size: 9.5px; color: #111; border-bottom: 1px solid #e5e7eb; border-right: 1px solid #e5e7eb; vertical-align: top; word-wrap: break-word; }
    .ops-table td:last-child { border-right: none; }
    .ops-table tr.req td { background: #f3f4f6 !important; font-weight: 600; border-top: 1.5px solid #9ca3af; }
    .ops-table tr.req .sub { font-weight: normal; color: #4b5563; font-size: 8.5px; margin-top: 2px; }
    .ops-table tr.ln td { font-size: 9px; color: #374151; padding-top: 3px; padding-bottom: 3px; }
    .doc { font-family: monospace; font-size: 10px; }
    .code { font-family: monospace; color: #6b7280; }
    .uom { color: #9ca3af; font-size: 8px; }
    .stage { display: inline-block; border: 1px solid #374151; border-radius: 3px; padding: 1px 5px; font-size: 8.5px; }
    .lstage { color: #6b7280; font-size: 8.5px; }
    .right { text-align: right; } .center { text-align: center; }
    .totals-wrap { display: flex; justify-content: flex-end; margin-bottom: 16px; page-break-inside: avoid; }
    .totals-grid { width: 320px; border: 1px solid #d1d5db; }
    .totals-row { display: grid; grid-template-columns: 1fr auto; padding: 5px 12px; font-size: 11px; border-bottom: 1px solid #e5e7eb; }
    .totals-row:last-child { border-bottom: none; }
    .totals-row .t-label { color: #6b7280; } .totals-row .t-value { font-weight: 600; text-align: right; color: #111; }
    .totals-row.grand { background: #374151 !important; padding: 8px 12px; }
    .totals-row.grand .t-label, .totals-row.grand .t-value { color: #fff !important; font-weight: 700; font-size: 12px; }
    .doc-footer { page-break-inside: avoid; padding-top: 20px; }
    @media print { .doc-footer { position: fixed; bottom: 0; left: 0; right: 0; padding: 0 14mm 6mm; background: #fff; } }
    .system-generated { text-align: center; margin-top: 10px; font-size: 8px; color: #9ca3af; letter-spacing: 0.06em; }
    .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-35deg); width: 55%; opacity: 0.06; pointer-events: none; z-index: 0; }
  </style>
</head>
<body>
  <img src="${watermarkUrl}" class="watermark" alt="" />
  <div id="toolbar">
    <span class="tb-title">${title} &mdash; ${period}</span>
    <button class="btn-pdf" onclick="window.print()">&#128438;&nbsp;Download PDF</button>
    <button class="btn-download" onclick="downloadHtml()">&#8595;&nbsp;Download HTML</button>
    <button class="btn-close" onclick="window.close()">&#10005;&nbsp;Close</button>
  </div>
  <div class="tb-spacer"></div>

  <div class="doc-main">
    <div class="top-banner">
      <div class="company-block">
        <img src="${logoUrl}" alt="Logo" class="company-logo" />
        ${locationInfo.name ? `<div class="company-name">${e(locationInfo.name)}</div>` : ''}
        ${locationInfo.street ? `<div class="company-detail">${e(locationInfo.street)}</div>` : ''}
        ${locationInfo.city ? `<div class="company-detail">${e(locationInfo.city)}</div>` : ''}
        ${locationInfo.zipCode ? `<div class="company-detail">${e(locationInfo.zipCode)}</div>` : ''}
      </div>
      <div class="doc-block">
        <div class="doc-title">Stock Operations Report</div>
        <div class="doc-subtitle">${isTransfers ? 'Transfers' : 'Material Requests'} &middot; ${flowLabel}</div>
        <div class="doc-subtitle">${period}</div>
      </div>
    </div>

    <div class="meta-row">
      <div class="meta-cell"><label>From Date</label><span>${filters.dateFrom ? fmtDate(filters.dateFrom) : 'All'}</span></div>
      <div class="meta-cell"><label>To Date</label><span>${filters.dateTo ? fmtDate(filters.dateTo) : 'All'}</span></div>
      <div class="meta-cell"><label>Warehouse</label><span>${e(whName)}</span></div>
      <div class="meta-cell"><label>Stage / Status</label><span>${e(filters.stage || 'All')} / ${e(filters.status ? pretty(filters.status) : 'All')}</span></div>
      <div class="meta-cell"><label>Generated On</label><span>${generatedAt}</span></div>
    </div>

    ${stages ? `<div class="stages">${stages}</div>` : ''}

    <div class="section-title">${isTransfers ? 'Transfer Requests' : 'Material Requests'} (${data.rows.length})${search ? ' &middot; search: ' + e(search) : ''}</div>
    <table class="ops-table">
      <colgroup>${colgroup}</colgroup>
      <thead><tr>${thead}</tr></thead>
      <tbody>${body || `<tr><td colspan="${cols}" style="text-align:center;color:#9ca3af;padding:14px">No requests</td></tr>`}</tbody>
    </table>

    <div class="totals-wrap">
      <div class="totals-grid">
        ${totals.map(([l, v]) => `<div class="totals-row"><span class="t-label">${l}</span><span class="t-value">${v}</span></div>`).join('')}
        <div class="totals-row grand"><span class="t-label">${grand[0]}</span><span class="t-value">${grand[1]}</span></div>
      </div>
    </div>
  </div>

  <div class="doc-footer">
    <div class="system-generated">&#128274;&nbsp;This document is system generated &mdash; ${generatedAt}</div>
  </div>

  <script>
    function downloadHtml() {
      var a = document.createElement('a');
      a.href = 'data:text/html;charset=utf-8,' + encodeURIComponent(document.documentElement.outerHTML);
      a.download = 'stock-operations-${tab}.html';
      a.click();
    }
  <\/script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) {
      toast.error('Pop-up blocked! Please allow pop-ups for this site to print reports.');
      URL.revokeObjectURL(url);
      return;
    }
    win.onload = () => setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const card = (label, value, cls = 'text-gray-900') => (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-3">
      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold tabular-nums ${cls}`}>{value}</p>
    </div>
  );
  const chip = (d, extra = '') => (
    <span key={d._id} className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-medium whitespace-nowrap ${docColor(d.status)}`} title={`${pretty(d.status)}${d.date ? ` · ${fmtDate(d.date)}` : ''}${d.by ? ` · ${d.by}` : ''}${d.note ? ` · ${d.note}` : ''}`}>
      {d.number}{extra}
    </span>
  );
  const num = (n, cls = '') => <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${cls}`}>{n ? qty(n) : <span className="text-gray-300">0</span>}</td>;

  const flowStep = (title, items, empty) => (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">{title}</p>
      {items.length ? (
        <div className="space-y-1">{items}</div>
      ) : <p className="text-xs text-gray-400">{empty}</p>}
    </div>
  );
  const flowDoc = (d, detail) => (
    <div key={d._id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
      {chip(d)}
      <span className="text-gray-500">{pretty(d.status)} · {fmtDate(d.date)}{d.by ? ` · ${d.by}` : ''}</span>
      {detail && <span className="text-gray-700">{detail}</span>}
      {d.note && <span className="text-red-600">{d.note}</span>}
    </div>
  );

  const expandedTransfer = (r) => (
    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {flowStep('1. Transfer Request', [flowDoc({ _id: r._id, number: r.requestNo, status: r.status, date: r.requestDate, by: r.requestedBy, note: r.note }, `${r.priority === 'urgent' ? 'Urgent · ' : ''}${pretty(r.procurementType)}`)], '')}
        {flowStep('2. Goods Issue Notes', r.issueNotes.map(g => flowDoc(g, `${qty(g.quantity)} issued`)), 'No issue note yet')}
        {flowStep('3. Transfer In (receipt)', r.receipts.map(t => flowDoc(t, `${qty(t.quantity)} received${t.short ? `, ${qty(t.short)} short` : ''}`)), 'Not received yet')}
      </div>
      <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-white text-[11px] text-gray-500 uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Item</th>
              {['Requested', 'Approved', 'Issued', 'Received', 'Short', 'In Transit', 'Pending Issue', 'Issued Value'].map(h => <th key={h} className="px-3 py-2 text-right whitespace-nowrap">{h}</th>)}
              <th className="px-3 py-2 text-left">Stage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {r.lines.map(l => (
              <tr key={l._id}>
                <td className="px-3 py-2"><div className="font-medium text-gray-900">{l.name}</div><div className="text-xs text-gray-500">{l.code}{l.uom ? ` · ${l.uom}` : ''}</div></td>
                {num(l.requested)}{num(l.approved)}{num(l.issued, 'text-indigo-700')}{num(l.received, 'text-green-700')}{num(l.short, 'text-red-600')}{num(l.inTransit, 'text-blue-700')}{num(l.pendingIssue, 'text-orange-600')}
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{l.value ? money(l.value) : '—'}</td>
                <td className="px-3 py-2"><span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${stageColor(l.stage)}`}>{l.stage}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const expandedMaterial = (r) => (
    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {flowStep('1. Material Request', [flowDoc({ _id: r._id, number: r.requestNo, status: r.status, date: r.requestDate, by: r.requestedBy, note: r.note }, r.costCenter ? `Cost center ${r.costCenter}` : '')], '')}
        {flowStep('2. Released (Consumption)', r.releases.map(c => flowDoc({ ...c, status: 'approved' }, `${qty(c.quantity)} released · ${money(c.cost)}${c.gl ? ` · GL ${c.gl}` : ''}`)), 'Nothing released yet')}
      </div>
      <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-white text-[11px] text-gray-500 uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Item</th>
              {['Requested', 'Released', 'Pending', 'Cost'].map(h => <th key={h} className="px-3 py-2 text-right">{h}</th>)}
              <th className="px-3 py-2 text-left">Stage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {r.lines.map(l => (
              <tr key={l._id}>
                <td className="px-3 py-2"><div className="font-medium text-gray-900">{l.name}</div><div className="text-xs text-gray-500">{l.code}{l.uom ? ` · ${l.uom}` : ''}</div></td>
                {num(l.requested)}{num(l.released, 'text-green-700')}{num(l.pending, 'text-orange-600')}
                <td className="px-3 py-2 text-right tabular-nums">{l.cost ? money(l.cost) : '—'}</td>
                <td className="px-3 py-2"><span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${stageColor(l.stage)}`}>{l.stage}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const inputCls = 'px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors';
  const colCount = isTransfers ? 10 : 8;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Header + filters */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pt-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-5 py-3 flex flex-wrap items-center justify-between gap-3 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <ArrowsRightLeftIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-white">Stock Operations Report</h2>
                <p className="text-indigo-200 text-xs">Whole flow of every stock request, stage by stage</p>
              </div>
            </div>
            <div className="flex bg-white/15 rounded-lg p-0.5">
              {TABS.map(t => (
                <button key={t.key} onClick={() => switchTab(t.key)} title={t.sub}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${tab === t.key ? 'bg-white text-indigo-700 shadow-sm' : 'text-white/90 hover:bg-white/10'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="px-5 py-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5 text-indigo-400" />From</label>
              <input type="date" value={filters.dateFrom} onChange={e => setFilter('dateFrom', e.target.value)} className={inputCls} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5 text-indigo-400" />To</label>
              <input type="date" value={filters.dateTo} onChange={e => setFilter('dateTo', e.target.value)} className={inputCls} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Stage</label>
              <select value={filters.stage} onChange={e => setFilter('stage', e.target.value)} className={`${inputCls} min-w-[170px]`}>
                <option value="">All stages</option>
                {STAGE_OPTIONS[tab].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Document Status</label>
              <select value={filters.status} onChange={e => setFilter('status', e.target.value)} className={`${inputCls} min-w-[160px]`}>
                {STATUS_OPTIONS[tab].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Warehouse</label>
              <select value={filters.warehouseId} onChange={e => setFilter('warehouseId', e.target.value)} className={`${inputCls} min-w-[170px] max-w-[240px]`}>
                <option value="">All warehouses</option>
                {(Array.isArray(warehouses) ? warehouses : []).map(w => <option key={w._id} value={w._id}>{w.name || w.locationName || w.code}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Search</label>
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
                  placeholder={isTransfers ? 'Request / issue / receipt no or item...' : 'MRN / consumption no, cost center or item...'}
                  className={`${inputCls} w-full pl-9`} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={reset} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Reset</button>
              <button type="button" onClick={printReport} disabled={!data.rows.length}
                className="px-3 py-2 text-sm font-medium text-indigo-700 bg-white border border-indigo-300 rounded-lg hover:bg-indigo-50 disabled:opacity-50 inline-flex items-center gap-1.5">
                <PrinterIcon className="w-4 h-4" /> Print / PDF
              </button>
              <button type="button" onClick={exportExcel} disabled={!data.rows.length}
                className="px-3 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                <ArrowDownTrayIcon className="w-4 h-4" /> Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow overflow-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        {/* Summary */}
        {isTransfers ? (
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            {card('Requests', summary.requests || 0, 'text-indigo-700')}
            {card('Requested', qty(summary.requested))}
            {card('Issued', qty(summary.issued), 'text-indigo-700')}
            {card('Received', qty(summary.received), 'text-green-700')}
            {card('In Transit', qty(summary.inTransit), 'text-blue-700')}
            {card('Pending Issue', qty(summary.pendingIssue), 'text-orange-600')}
            {card('Issued Value', money(summary.value), 'text-purple-700')}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {card('Requests', summary.requests || 0, 'text-indigo-700')}
            {card('Requested', qty(summary.requested))}
            {card('Released', qty(summary.released), 'text-green-700')}
            {card('Pending', qty(summary.pending), 'text-orange-600')}
            {card('Cost', money(summary.cost), 'text-purple-700')}
          </div>
        )}
        {summary.byStage && Object.keys(summary.byStage).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(summary.byStage).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
              <button key={s} onClick={() => setFilter('stage', filters.stage === s ? '' : s)}
                className={`px-2.5 py-1 rounded-full border text-xs font-medium ${stageColor(s)} ${filters.stage === s ? 'ring-2 ring-offset-1 ring-indigo-400' : ''}`}>
                {s}: {n}
              </button>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-800">
              {isTransfers ? 'Transfer Requests' : 'Material Requests'} ({data.rows.length}){data.truncated ? ' — first 1000, narrow the filters' : ''}
            </span>
            {data.rows.length > 0 && (
              <button onClick={toggleAll} className="text-xs font-medium text-indigo-700 hover:text-indigo-900">{allOpen ? 'Collapse all' : 'Expand all'}</button>
            )}
          </div>
          {loading ? (
            <div className="py-16"><LoadingSpinner size="lg" fullScreen={false} /></div>
          ) : error ? (
            <p className="px-4 py-10 text-center text-sm text-red-600">{error}</p>
          ) : data.rows.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-gray-500">No requests for these filters</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-white text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  {isTransfers ? (
                    <tr>
                      <th className="w-8 px-2 py-2.5" />
                      <th className="px-3 py-2.5 text-left">Request</th>
                      <th className="px-3 py-2.5 text-left">From → To</th>
                      <th className="px-3 py-2.5 text-left">Issue Notes</th>
                      <th className="px-3 py-2.5 text-left">Transfer In</th>
                      <th className="px-3 py-2.5 text-right">Requested</th>
                      <th className="px-3 py-2.5 text-right">Issued</th>
                      <th className="px-3 py-2.5 text-right">Received</th>
                      <th className="px-3 py-2.5 text-right whitespace-nowrap">In Transit</th>
                      <th className="px-3 py-2.5 text-left">Stage</th>
                    </tr>
                  ) : (
                    <tr>
                      <th className="w-8 px-2 py-2.5" />
                      <th className="px-3 py-2.5 text-left">Request</th>
                      <th className="px-3 py-2.5 text-left">Cost Center / Warehouse</th>
                      <th className="px-3 py-2.5 text-left">Released (Consumption)</th>
                      <th className="px-3 py-2.5 text-right">Requested</th>
                      <th className="px-3 py-2.5 text-right">Released</th>
                      <th className="px-3 py-2.5 text-right">Pending</th>
                      <th className="px-3 py-2.5 text-left">Stage</th>
                    </tr>
                  )}
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.rows.map(r => {
                    const open = expanded.has(r._id);
                    return (
                      <React.Fragment key={r._id}>
                        <tr className={`hover:bg-gray-50 cursor-pointer ${open ? 'bg-indigo-50/40' : ''}`} onClick={() => toggle(r._id)}>
                          <td className="px-2 py-2.5 text-center">
                            <span className={`inline-flex items-center justify-center w-5 h-5 rounded border text-xs font-bold ${open ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-500'}`}>{open ? '−' : '+'}</span>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <div className="font-semibold text-gray-900">{r.requestNo}</div>
                            <div className="text-xs text-gray-500">{fmtDate(r.requestDate)}{r.requestedBy ? ` · ${r.requestedBy}` : ''}</div>
                          </td>
                          {isTransfers ? (
                            <>
                              <td className="px-3 py-2.5 text-xs text-gray-700 min-w-[220px]">
                                <div>{nameOf(r.source, r.sourceIds)}</div>
                                <div className="text-gray-500">→ {nameOf(r.destination, r.destinationIds)}</div>
                              </td>
                              <td className="px-3 py-2.5"><div className="flex flex-wrap gap-1">{r.issueNotes.length ? r.issueNotes.map(g => chip(g)) : <span className="text-xs text-gray-400">—</span>}</div></td>
                              <td className="px-3 py-2.5"><div className="flex flex-wrap gap-1">{r.receipts.length ? r.receipts.map(t => chip(t)) : <span className="text-xs text-gray-400">—</span>}</div></td>
                              {num(r.totals.requested)}{num(r.totals.issued, 'text-indigo-700 font-medium')}{num(r.totals.received, 'text-green-700 font-medium')}{num(r.totals.inTransit, 'text-blue-700 font-medium')}
                            </>
                          ) : (
                            <>
                              <td className="px-3 py-2.5 text-xs text-gray-700 min-w-[200px]">
                                <div>{r.costCenter || '—'}</div>
                                <div className="text-gray-500">{nameOf(r.warehouse, r.warehouseIds)}</div>
                              </td>
                              <td className="px-3 py-2.5"><div className="flex flex-wrap gap-1">{r.releases.length ? r.releases.map(c => chip({ ...c, status: 'approved' })) : <span className="text-xs text-gray-400">—</span>}</div></td>
                              {num(r.totals.requested)}{num(r.totals.released, 'text-green-700 font-medium')}{num(r.totals.pending, 'text-orange-600 font-medium')}
                            </>
                          )}
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold ${stageColor(r.stage)}`}>{r.stage}</span>
                            <div className="text-[11px] text-gray-400 mt-0.5">{pretty(r.status)}</div>
                          </td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={colCount} className="p-0">{isTransfers ? expandedTransfer(r) : expandedMaterial(r)}</td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockOperationsReport;
